import { Card, Stack, CircularProgress, Dropdown, Menu, MenuButton, MenuItem, Button, Typography, Tooltip, CardContent, CardActions, IconButton } from '@mui/joy';
import { FilterAlt, Search } from '@mui/icons-material';
import DecayGraph from './Graph';
import List from '@mui/joy/List';
import ListItem from '@mui/joy/ListItem';
import { VariableSizeList } from 'react-window';
import { useSelector, useDispatch } from 'react-redux'
import { useState, useEffect, useCallback } from 'react';
import { API_PATH } from '../Internal/api.js';
import { extractIsobars } from '../Internal/helpers.js';
import { applyClientFilters } from '../Internal/filterLogic.js';


export default function TreeList() {

    const [containerHeight, setContainerHeight] = useState(window.innerHeight - 80);
    const containerRef = useCallback((node) => {
        if (!node) return;
        const observer = new ResizeObserver(([entry]) => setContainerHeight(entry.contentRect.height));
        observer.observe(node);
    }, []);

    const filter = useSelector((state) => state.topologyFilter);
    const [filterReady, setFilterReady] = useState(false);
    const [filteredTopologies, setFilteredTopologies] = useState([]);
    const mainGraph = useSelector((state) => state.mainGraph);
    const isobarViewFilter = useSelector((state) => state.isobarViewFilter);
    const n = useSelector((state) => state.decay);
    const globalIsobars = useSelector((state) => state.intermediateState.isobars);
    const checkedGraphs = useSelector((state) => state.checkedGraphs);
    const clientFilters = useSelector((state) => state.clientFilters);
    const [dropdownOpen, setDropdownOpen] = useState(false);

    useEffect(() => {
        dispatch({ type: 'CLEAR_FILTERS' });
        setFilterReady(false);
        setFilteredTopologies([]);
    }, [n]);

    useEffect(() => {
        if (Object.keys(filter).length === 0) {
            setFilterReady(false);
            setFilteredTopologies([]);
            return;
        }
        setFilterReady(false);
        const filterString = "[" + Object.keys(filter).join(",") + "]";
        fetch(API_PATH + "filtered?n=" + n + "&filter=" + filterString)
            .then(r => { if (!r.ok) throw new Error(); return r.json(); })
            .then(data => { setFilteredTopologies(data.topologies); setFilterReady(true); })
            .catch(console.error);
    }, [filter]);

    const graphs = useSelector((state) => state.graph);
    

    const dispatch = useDispatch();
    const handleGraphCardClick = (tuple) => {
        if (! dropdownOpen) {
            dispatch({ type: 'SET_MAIN_GRAPH', payload: tuple });
        }
    };

    if (!graphs.hasOwnProperty('topologies')) {
        return (
            <List>
            </List>
        );
    }
    if (!filterReady && !(Object.keys(filter).length === 0)) {
        return (
            <CircularProgress />
        )
    }





    const COLUMNS_WITH_GRAPH = 3;
    const COLUMNS_WITHOUT_GRAPH = 3;

    const items = applyClientFilters(
        filterReady ? filteredTopologies : graphs.topologies,
        clientFilters, globalIsobars, checkedGraphs
    );
    const graphOpen = mainGraph && Object.keys(mainGraph).length > 0;
    const columns = graphOpen ? COLUMNS_WITH_GRAPH : COLUMNS_WITHOUT_GRAPH;

    const buttonComponent = ({index}) => {
        const tuple = items[index];
        return (
            <Card variant={(mainGraph.tuple === items[index]) ? 'soft' : 'outlined'}  sx={{
                width: '100%',
                border: (mainGraph.tuple === items[index]) ? '4px solid #ccc' : '1px solid #ccc',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}>
                <CardContent sx={{ flex: '1 1 auto', minHeight: 0, p: 0, position: 'relative' }}>
                    <Tooltip title="Inspect decay graph" placement="top">
                        <IconButton
                            size="sm"
                            variant="soft"
                            color="neutral"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleGraphCardClick(items[index]);
                                dispatch({ type: 'SET_TREE_MASK', payload: {} });
                                dispatch({ type: 'SHOW_MAIN_GRAPH' });
                            }}
                            sx={{ position: 'absolute', top: 4, right: 4, zIndex: 10 }}
                        >
                            <Search fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Button
                        onClick={() => {
                            handleGraphCardClick(items[index]);
                            dispatch({ type: 'SET_TREE_MASK', payload: {} });
                        }}
                        color={(mainGraph.tuple === items[index]) ? null : null}
                        sx={{ width: '100%', height: '100%', p: 0 }}
                    >
                        <DecayGraph tuple={tuple} />
                    </Button>
            </CardContent>
            <CardActions sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {ChainInfo({index})}
            <Tooltip title={isobarViewFilter === items[index] ? "Show all isobars" : "Show only isobars of this topology"}>
                <IconButton
                    size="sm"
                    variant={isobarViewFilter === items[index] ? 'solid' : 'soft'}
                    color={isobarViewFilter === items[index] ? 'primary' : 'neutral'}
                    onClick={(e) => {
                        e.stopPropagation();
                        dispatch({
                            type: isobarViewFilter === items[index] ? 'CLEAR_ISOBAR_VIEW_FILTER' : 'SET_ISOBAR_VIEW_FILTER',
                            payload: items[index],
                        });
                    }}
                >
                    <FilterAlt fontSize="small" />
                </IconButton>
            </Tooltip>
            </CardActions>
            </Card>
        )
    }

    const ChainInfo = ({index}) => {
        const tuple = items[index];
        const isobars = {};

        extractIsobars(tuple[0], isobars);
        extractIsobars(tuple[1], isobars);
        const completeChains = Object.keys(isobars).map((isobar) => {
            return Object.keys(globalIsobars[isobar].resonances).length;
        });

        // check for any number not 0 in completeChains

        // const anyNonZero = completeChains.reduce((prev, curr) => (curr != 0) || prev, false);
        const totalChains = completeChains.reduce((prev, curr) => curr * prev, 1);
        
        // var stateColor = (anyNonZero && totalChains === 0) ? 'warning' : 'neutral';
        var stateColor = 'neutral';
        const relatedTrees = ((checkedGraphs.hasOwnProperty("trees")) ?
            checkedGraphs.trees.filter(tree => tree.topology == JSON.stringify(items[index])) : []);

            
        if (relatedTrees.length > 0) {
            if ( relatedTrees.every(tree => tree.check) ){
                stateColor = 'success';
            }
            else if ( relatedTrees.some(tree => tree.check) ){
                stateColor = 'warning';
            }
            else if ( relatedTrees.every(tree => !tree.check) ){
                stateColor = 'danger';
            }
        }
        
        // console.log(relatedTrees);

        return (
            <Dropdown onOpen={() => setDropdownOpen(!dropdownOpen)}>
                <MenuButton color={stateColor}>
                    Number of complete Chains: {totalChains}
                </MenuButton>
                <Menu>
                    {
                        relatedTrees.map((tree, ind) => (
                            <Tooltip title={tree.check ? "All Good" : "Decays marked in red are not possible with given P and J values"} >
                                <MenuItem key={ind}>
                                <Button onClick={
                                            () => {
                                                dispatch({ type: 'SET_TREE_MASK', payload: tree.resonances });
                                                handleGraphCardClick(items[index]);
                                                console.log(tree.resonances);
                                            }
                                        } color={null}
                                        >
                                    {Object.keys(tree.resonances).map((resonanceKey, resIndex) => (
                                            <Typography 
                                                key={resIndex}
                                                // color ={tree.check ? 'success' : 'danger'}
                                                color={tree.failed.find((element) => element == resonanceKey) == resonanceKey ? 'danger' : 'success'}
                                            >
                                                {tree.resonances[resonanceKey].name + (resIndex < (Object.keys(tree.resonances).length - 1) ? ", " : "")}
                                            </Typography>
                                    ))}
                                </Button>
                                </MenuItem>
                            </Tooltip>
                        ))
                    }
                    
                </Menu>
            </Dropdown>
        )
    };
    

    const findMultiVertex = (topology) => {
        if (Array.isArray(topology)) {
            if (Array.isArray(topology[0]) && Array.isArray(topology[1])) return true;
            return findMultiVertex(topology[0]) || findMultiVertex(topology[1]);
        }
        return false;
    };

    const getItemSize = (index) => findMultiVertex(items[index]) ? 300 : 250;

    const rowCount = Math.ceil(items.length / columns);

    const getRowSize = (rowIndex) => {
        let max = 0;
        for (let c = 0; c < columns; c++) {
            const itemIndex = rowIndex * columns + c;
            if (itemIndex < items.length) max = Math.max(max, getItemSize(itemIndex));
        }
        return max;
    };

    const Row = ({ index: rowIndex, style }) => (
        <ListItem style={{ ...style, display: 'flex', flexDirection: 'row', gap: '8px', padding: '4px 8px', boxSizing: 'border-box' }}>
            {Array.from({ length: columns }).map((_, c) => {
                const itemIndex = rowIndex * columns + c;
                if (itemIndex >= items.length) return <div key={c} style={{ flex: 1 }} />;
                return (
                    <div key={c} style={{ flex: 1, minWidth: 0, height: '100%' }}>
                        {buttonComponent({ index: itemIndex })}
                    </div>
                );
            })}
        </ListItem>
    );

    return (
        <div ref={containerRef} style={{ height: '100%', width: '100%', overflow: 'hidden' }}>
            <VariableSizeList
                key={columns}
                height={containerHeight}
                itemCount={rowCount}
                itemSize={getRowSize}
                width="100%"
            >
                {Row}
            </VariableSizeList>
        </div>
    );
    }